// import components
import { TextField, TextFieldProps } from '@mui/material';
import { t } from 'i18next';

// import types
import { ChangeEvent } from 'react';

// import utils
import { Controller, Control } from 'react-hook-form';

type ControlledTimeFieldProps = Omit<TextFieldProps, 'name' | 'onChange'> & {
  name: string;
  control: Control<any>;
};

// A controlled TextField that enforces HH:mm format
export default function ControlledTimeField({
  name,
  control,
  ...textFieldProps
}: ControlledTimeFieldProps) {
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    onChangeFn: (value: string) => void
  ) => {
    // remove any non-digit, non-colon characters
    const rawValue = e.target.value.replace(/[^\d:]/g, '');

    // if user typed more than one colon, remove extras
    const parts = rawValue.split(':');
    const singleColonValue =
      parts.length > 2 ? `${parts[0]}:${parts[1]}` : rawValue;

    // temporarily remove that single colon, to re-insert at correct place
    const withoutColon = singleColonValue.replace(':', '');

    // insert the colon automatically after 2 digits (if we have 3+ chars)
    const colonInserted =
      withoutColon.length > 2
        ? `${withoutColon.slice(0, 2)}:${withoutColon.slice(2)}`
        : withoutColon;

    // enforce max length of 5 (HH:mm)
    const finalValue =
      colonInserted.length > 5 ? colonInserted.slice(0, 5) : colonInserted;

    onChangeFn(finalValue);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...textFieldProps}
          value={field.value || ''}
          onChange={(e) => handleChange(e, field.onChange)}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          placeholder={t('hhmmFormat')}
        />
      )}
    />
  );
}
